/**
 * Shared mappings between `usePivotState`'s UI-facing state and the shapes
 * `TransactionsExplorer` expects.
 *
 * Both dense table pages (`/transactions` → TenantTransactionsV2Page and
 * `/snapshots` → SnapshotPage) drive the same explorer from the same
 * `usePivotState` hook, so these translations were previously duplicated
 * byte-for-byte in both files. They live here so a new pivot mode or view
 * mode only has to be added once.
 *
 * @fileoverview Pivot/view-mode mappings shared by the transactions pages
 */

import type { GroupByOption } from '../../hooks/useFinancialMatrix';
import type { FilterMode, PivotMode, ViewMode } from './FilterBar';

/** The explorer's own view-mode vocabulary, which is wider than the bar's. */
export type ExplorerViewMode = 'table' | 'pivot' | 'flow' | 'chart' | 'payments' | 'grouped';

export const pivotModeToGroupBy = (mode: PivotMode): GroupByOption => ({
  vendor_month:      'vendor',
  vendor_year:       'vendor_year',
  merchant_month:    'merchant',
  merchant_year:     'merchant_year',
  category_month:    'category',
  category_year:     'category_year',
  subcategory_month: 'subcategory',
  subcategory_year:  'subcategory_year',
  total_month:       'month',
  total_year:        'year',
} as Record<PivotMode, GroupByOption>)[mode];

/**
 * A payment-channel filter (checks/zelle/transfers) overrides the chosen
 * view: those channels are only meaningful in the payments report.
 */
export const viewModeToExplorer = (mode: ViewMode, filterMode: FilterMode): ExplorerViewMode => {
  if (filterMode === 'checks' || filterMode === 'zelle' || filterMode === 'transfer') return 'payments';
  return ({
    transactions: 'table',
    pivot:        'pivot',
    flow:         'flow',
    balance:      'chart',
  } as const)[mode];
};

/** Order-insensitive comparison — the selector hands back ids in any order. */
export const areSameAccountSelection = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false;
  const leftSet = new Set(left);
  return right.every((id) => leftSet.has(id));
};

/**
 * Tracks an account-selection change through to the explorer finishing its
 * refetch, so the selector can show a spinner for the whole round trip
 * rather than only while its own state settles.
 */
export type AccountSelectionPhase = 'idle' | 'pending' | 'loading';
